import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { LogOut, ShoppingBag, UserRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useToast } from "../context/ToastContext";
import { db } from "../firebase";
import { CHECKOUT_DRAFT_KEY } from "../lib/stripeCheckout";
import {
  BOOKING_STATUSES,
  LATDIOR_ORDER_STATUSES,
  ORDER_STATUSES,
  formatCurrency,
} from "../lib/platformData";

function sortRecent(list) {
  return [...list].sort((a, b) => Number(b.createdAt?.seconds || 0) - Number(a.createdAt?.seconds || 0));
}

function StatusBadge({ value, labels }) {
  return (
    <span className="inline-flex rounded-full border border-white/15 px-3 py-1 text-xs font-bold uppercase text-gray-200">
      {labels?.[value] || value || "en attente"}
    </span>
  );
}

function EmptyState({ children, to, label }) {
  return (
    <div className="rounded border border-white/10 bg-black p-5 text-sm text-gray-400">
      {children}
      {to && (
        <Link to={to} className="mt-4 inline-flex rounded-full bg-white px-4 py-2 text-xs font-bold uppercase text-black">
          {label}
        </Link>
      )}
    </div>
  );
}

export default function AccountPage() {
  const { user, loading, logout } = useAuth();
  const { clearCart } = useCart();
  const toast = useToast();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [latdiorOrders, setLatdiorOrders] = useState([]);

  useEffect(() => {
    if (!user || new URLSearchParams(location.search).get("payment") !== "success") return;
    let draft;
    try {
      draft = JSON.parse(window.sessionStorage.getItem(CHECKOUT_DRAFT_KEY) || "null");
    } catch {
      return;
    }
    if (draft?.userId !== user.uid) return;
    const payable = (draft.orderRefs || []).filter((ref) => ref.type !== "studioBooking");
    if (!payable.length) return;
    const paid = payable.every((ref) => {
      const list = ref.type === "serviceOrder" ? serviceOrders : latdiorOrders;
      return list.some((order) => order.id === ref.id && order.paymentStatus === "paid");
    });
    if (!paid) return;
    window.sessionStorage.removeItem(CHECKOUT_DRAFT_KEY);
    clearCart();
    toast.success("Paiement confirmé", "Ta commande est visible dans ton compte.");
  }, [user, location.search, serviceOrders, latdiorOrders, clearCart, toast]);

  useEffect(() => {
    if (!user) return undefined;

    const offBookings = onSnapshot(
      query(collection(db, "studioBookings"), where("userId", "==", user.uid)),
      (snap) => setBookings(sortRecent(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
    );
    const offServices = onSnapshot(
      query(collection(db, "serviceOrders"), where("userId", "==", user.uid)),
      (snap) => setServiceOrders(sortRecent(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
    );
    const offLatdior = onSnapshot(
      query(collection(db, "latdiorOrders"), where("userId", "==", user.uid)),
      (snap) => setLatdiorOrders(sortRecent(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
    );

    return () => {
      offBookings();
      offServices();
      offLatdior();
    };
  }, [user]);

  const hasHistory = useMemo(
    () => bookings.length || serviceOrders.length || latdiorOrders.length,
    [bookings, serviceOrders, latdiorOrders]
  );

  if (loading) {
    return <main className="min-h-screen bg-black px-6 py-24 text-white">Chargement...</main>;
  }

  if (!user) {
    return (
      <main className="min-h-screen bg-black px-6 py-24 text-white md:px-20">
        <section className="mx-auto max-w-3xl rounded-lg border border-white/10 bg-zinc-950 p-6 md:p-8">
          <p className="mb-3 text-sm font-bold uppercase tracking-[0.25em] text-red-500">
            Compte requis
          </p>
          <h1 className="text-4xl font-black uppercase md:text-6xl">Connecte-toi</h1>
          <p className="readable-copy mt-4 text-gray-400">
            Un compte est obligatoire pour reserver, acheter ou suivre une commande.
          </p>
          <Link
            to="/connexion"
            state={{ from: location.pathname }}
            className="mt-8 inline-flex rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black"
          >
            Connexion
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black px-6 py-24 text-white md:px-20">
      <section className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-col justify-between gap-4 rounded-lg border border-white/10 bg-zinc-950 p-5 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            {user.photoURL ? (
              <img src={user.photoURL} alt="" className="h-12 w-12 rounded-full object-cover" />
            ) : (
              <div className="grid h-12 w-12 place-items-center rounded-full bg-white text-black">
                <UserRound size={22} />
              </div>
            )}
            <div>
              <p className="text-lg font-bold">{user.displayName || "Compte Unlimited"}</p>
              <p className="text-sm text-gray-400">{user.email}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/panier" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">
              <ShoppingBag size={16} /> Panier
            </Link>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-bold uppercase">
              <LogOut size={16} /> Deconnexion
            </button>
          </div>
        </div>

        <div className="mb-10">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-red-500">
            Espace utilisateur
          </p>
          <h1 className="text-5xl font-black uppercase leading-none md:text-7xl">
            Historique et statuts.
          </h1>
        </div>

        {!hasHistory && (
          <EmptyState to="/reservation-studio" label="Reserver une seance">
            Rien pour le moment. Lance une reservation studio, une commande service ou Latdior.
          </EmptyState>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <section className="rounded-lg border border-white/10 bg-zinc-950 p-5">
            <h2 className="mb-4 text-2xl font-black uppercase">Reservations studio</h2>
            <div className="space-y-3">
              {bookings.length === 0 && (
                <EmptyState to="/reservation-studio" label="Reserver">
                  Aucune reservation studio.
                </EmptyState>
              )}
              {bookings.map((booking) => (
                <article key={booking.id} className="rounded border border-white/10 bg-black p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-bold">{booking.date}</p>
                      <p className="text-sm text-gray-400">
                        {booking.sessionLabel || "Studio"} - {booking.startTime || booking.hours?.[0]} - {booking.endTime || ""}
                      </p>
                    </div>
                    <StatusBadge value={booking.status} labels={BOOKING_STATUSES} />
                  </div>
                  <p className="mt-3 text-sm text-gray-400">
                    Total: {formatCurrency(booking.total, booking.currency)}
                  </p>
                </article>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-white/10 bg-zinc-950 p-5">
            <h2 className="mb-4 text-2xl font-black uppercase">Services</h2>
            <div className="space-y-3">
              {serviceOrders.length === 0 && (
                <EmptyState to="/services" label="Commander">
                  Aucune commande mixage, mastering ou prod.
                </EmptyState>
              )}
              {serviceOrders.map((order) => (
                <article key={order.id} className="rounded border border-white/10 bg-black p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-bold">{order.serviceName}</p>
                      <p className="text-sm text-gray-400">{formatCurrency(order.total, order.currency)}</p>
                    </div>
                    <StatusBadge value={order.status} labels={ORDER_STATUSES} />
                  </div>
                  <p className="mt-3 text-xs uppercase tracking-wide text-gray-500">
                    Paiement: {order.paymentStatus || "pending_payment"}
                  </p>
                </article>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-white/10 bg-zinc-950 p-5">
            <h2 className="mb-4 text-2xl font-black uppercase">Latdior</h2>
            <div className="space-y-3">
              {latdiorOrders.length === 0 && (
                <EmptyState to="/latdior" label="Voir Latdior">
                  Aucune commande vetement.
                </EmptyState>
              )}
              {latdiorOrders.map((order) => (
                <article key={order.id} className="rounded border border-white/10 bg-black p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-bold">{(order.items || []).length} article(s)</p>
                      <p className="text-sm text-gray-400">{formatCurrency(order.total, order.currency)}</p>
                    </div>
                    <StatusBadge value={order.status} labels={LATDIOR_ORDER_STATUSES} />
                  </div>
                  <ul className="mt-3 space-y-1 text-sm text-gray-400">
                    {(order.items || []).map((item, index) => (
                      <li key={`${item.productId}-${index}`}>
                        {item.name} {item.size ? `- ${item.size}` : ""} x{item.quantity}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}
